import type { Product } from '@/types'
import { getEffectiveDisplayPrice } from '@/utils/currency'
import { getFallbackImageUrl } from '@/utils/images'

type SeoPayload = {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: 'website' | 'product' | 'article';
  robots?: string;
}

const DEFAULT_SITE_URL = 'https://walabi.com.py'
const DEFAULT_TITLE = 'Walabi Paraguay'
const DEFAULT_DESCRIPTION = 'Compra notebooks, celulares, tablets, consolas y accesorios con entrega por delivery en Paraguay.'
const DEFAULT_ROBOTS = 'index,follow'
const MANAGED_ATTRIBUTE = 'data-seo-managed'
const PRODUCT_SCHEMA_ID = 'walabi-product-schema'
const TRAILING_SLASH_PATHS = new Set([
  '/',
  '/products',
  '/categories',
  '/privacy-policy',
  '/terms-and-conditions',
  '/returns-policy',
  '/shipping-and-delivery',
  '/help-center',
])

function getSiteUrl() {
  return (import.meta.env.VITE_SITE_URL || DEFAULT_SITE_URL).replace(/\/+$/, '')
}

function buildAbsoluteUrl(path = '/') {
  return new URL(normalizeCanonicalPath(path), `${getSiteUrl()}/`).toString()
}

function normalizeCanonicalPath(path = '/') {
  if (!path || path === '/') {
    return '/'
  }

  const [pathnameWithQuery, hash = ''] = path.split('#')
  const [pathname, query = ''] = pathnameWithQuery.split('?')
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/'
  const canonicalPathname = TRAILING_SLASH_PATHS.has(normalizedPathname)
    ? `${normalizedPathname}/`
    : normalizedPathname

  const querySuffix = query ? `?${query}` : ''
  const hashSuffix = hash ? `#${hash}` : ''

  return `${canonicalPathname}${querySuffix}${hashSuffix}`
}

function stripHtml(value: string) {
  return value
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function truncate(value: string, maxLength = 160) {
  if (value.length <= maxLength) {
    return value
  }

  return `${value.slice(0, maxLength - 1).trimEnd()}…`
}

function upsertMeta(selector: string, attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    element.setAttribute(MANAGED_ATTRIBUTE, 'true')
    document.head.appendChild(element)
  }

  element.content = content
}

function upsertLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"][${MANAGED_ATTRIBUTE}="true"]`)

  if (!element) {
    element = document.createElement('link')
    element.rel = rel
    element.setAttribute(MANAGED_ATTRIBUTE, 'true')
    document.head.appendChild(element)
  }

  element.href = href
}

export function clearStructuredData(id = PRODUCT_SCHEMA_ID) {
  document.getElementById(id)?.remove()
}

export function injectStructuredData(id: string, payload: Record<string, unknown>) {
  clearStructuredData(id)

  const script = document.createElement('script')
  script.id = id
  script.type = 'application/ld+json'
  script.setAttribute(MANAGED_ATTRIBUTE, 'true')
  script.textContent = JSON.stringify(payload)
  document.head.appendChild(script)
}

export function applySeo(payload: SeoPayload) {
  const title = payload.title?.trim() || DEFAULT_TITLE
  const description = truncate(stripHtml(payload.description?.trim() || DEFAULT_DESCRIPTION))
  const canonicalUrl = buildAbsoluteUrl(payload.path || window.location.pathname)
  const robots = payload.robots || DEFAULT_ROBOTS
  const type = payload.type || 'website'
  const image = payload.image?.trim()

  document.title = title
  document.documentElement.lang = 'es-PY'

  upsertMeta('meta[name="description"]', 'name', 'description', description)
  upsertMeta('meta[name="robots"]', 'name', 'robots', robots)
  upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'Walabi Paraguay')
  upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', 'es_PY')
  upsertMeta('meta[property="og:type"]', 'property', 'og:type', type)
  upsertMeta('meta[property="og:title"]', 'property', 'og:title', title)
  upsertMeta('meta[property="og:description"]', 'property', 'og:description', description)
  upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl)
  upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', image ? 'summary_large_image' : 'summary')
  upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title)
  upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description)

  if (image) {
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', image)
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', image)
  }

  upsertLink('canonical', canonicalUrl)
}

function resolveProductImage(product: Product) {
  const candidate = product.images.find(Boolean) || product.default_image || getFallbackImageUrl('product')
  if (!candidate) {
    return ''
  }

  if (candidate.startsWith('http://') || candidate.startsWith('https://')) {
    return candidate
  }

  return buildAbsoluteUrl(candidate.startsWith('/') ? candidate : `/storage/${candidate}`)
}

export function applyProductSeo(product: Product, options: { title: string; description: string }) {
  const path = `/products/${encodeURIComponent(product.external_url || '')}`
  const image = resolveProductImage(product)
  const price = Math.round(getEffectiveDisplayPrice(product))
  const availability = product.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
  const canonicalPath = product.external_url ? path : window.location.pathname

  applySeo({
    title: `${options.title} | Walabi Paraguay`,
    description: options.description,
    path: canonicalPath,
    image,
    type: 'product',
  })

  injectStructuredData(PRODUCT_SCHEMA_ID, {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: options.title,
    description: truncate(stripHtml(options.description), 500),
    sku: product.sku || undefined,
    image: image ? [image] : undefined,
    brand: product.brand?.name
      ? {
          '@type': 'Brand',
          name: product.brand.name,
        }
      : undefined,
    category: product.category?.name || undefined,
    offers: {
      '@type': 'Offer',
      url: buildAbsoluteUrl(canonicalPath),
      priceCurrency: 'PYG',
      price,
      availability,
      itemCondition: 'https://schema.org/NewCondition',
    },
  })
}

export function applyProductListSeo(options: {
  title: string;
  description: string;
  path?: string;
  products: Product[];
}) {
  applySeo({
    title: options.title,
    description: options.description,
    path: options.path,
    type: 'website',
  })

  injectStructuredData(PRODUCT_SCHEMA_ID, {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: options.title,
    description: truncate(stripHtml(options.description), 300),
    numberOfItems: options.products.length,
    itemListElement: options.products.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: buildAbsoluteUrl(`/products/${encodeURIComponent(product.external_url || '')}`),
      name: product.name,
    })),
  })
}

export function applyDefaultSeo() {
  applySeo({
    title: `${DEFAULT_TITLE} | Hardware, Software y Accesorios`,
    description: DEFAULT_DESCRIPTION,
    path: window.location.pathname,
  })
  clearStructuredData()
}
